import * as React from "react";
import { Button, Chip, CircularProgress, IconButton, Tooltip } from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import GetAppIcon from "@material-ui/icons/GetApp";
import PowerSettingsNewIcon from "@material-ui/icons/PowerSettingsNew";
import {
  CreateButton,
  Datagrid,
  FunctionField,
  TextField,
  TopToolbar,
  useDataProvider,
  useListContext,
  useNotify,
  useRefresh,
} from "react-admin";
import { useHistory } from "react-router-dom";
import ToggleStatusDialog from "./ToggleStatusDialog";
import { DefaultPerPageSize } from "../../../Constants/Constant";
import CreatedByField from "../../../Components/ReferenceFields/CreatedBy/CreatedByField";
import UpdatedByField from "../../../Components/ReferenceFields/UpdatedBy/UpdatedByField";
import { CustomDateField } from "../../../Components/CustomDateField";
import FilterToolbar from "../../../Components/FiltersToolbar/FilterToolbar";
import { FilterInputType } from "../../../Enums/FilterTypes";
import PoolList from "../../../Components/PoolListComponent/PoolListComponent";
import { ExcelExport } from "../../../Helper/ExportExcel";
import { useVerifyPermission } from "@airborne/react-admin";

const EXCEL_HEADERS = [
  { header: "Owner Code", key: "ownerCode", width: 16 },
  { header: "PN", key: "pn", width: 22 },
  { header: "Description", key: "pnDescription", width: 40 },
  { header: "Vendor", key: "vendor", width: 28 },
  { header: "Is Active", key: "isActive", width: 14 },
  { header: "Created By", key: "createdBy", width: 22 },
  { header: "Created Date", key: "createdDate", width: 22 },
  { header: "Updated By", key: "updatedBy", width: 22 },
  { header: "Updated Date", key: "updatedDate", width: 22 },
];

const recordsToRows = (records: any[]) =>
  records.map(r => [
    r.ownerCode ?? "",
    r.pn ?? "",
    r.pnDescription ?? "",
    r.vendor ?? "",
    r.isActive ? "Active" : "Inactive",
    r.createdBy ?? "",
    r.createdDate ?? "",
    r.updatedBy ?? "",
    r.updatedDate ?? "",
  ]);

const ExportAllButton = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { filterValues, currentSort } = useListContext();
  const [loading, setLoading] = React.useState(false);

  const handleExport = () => {
    setLoading(true);
    dataProvider
      .getList("localrepaircomponents", {
        pagination: { page: 1, perPage: 10000 },
        sort: currentSort ?? { field: "createdDate", order: "DESC" },
        filter: filterValues ?? {},
      })
      .then(({ data }) => {
        ExcelExport(
          EXCEL_HEADERS,
          recordsToRows(data),
          "LocalRepairComponents",
          null,
          {
            rowStyleCondition: row => row[4] === "Inactive",
            rowStyle: {
              fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFF3E0" } },
              font: { color: { argb: "FFE65100" } },
            },
          },
          false
        );
      })
      .catch(() => notify("Export failed.", "error"))
      .finally(() => setLoading(false));
  };

  return (
    <Button
      size="small"
      color="primary"
      startIcon={loading ? <CircularProgress size={14} /> : <GetAppIcon />}
      onClick={handleExport}
      disabled={loading}
      style={{ marginLeft: 8 }}
    >
      {loading ? "Exporting..." : "Export Excel"}
    </Button>
  );
};

const ListActions = () => (
  <TopToolbar>
    <ExportAllButton />
    <CreateButton />
  </TopToolbar>
);

const LocalRepairComponentList = props => {
  useVerifyPermission("localrepaircomponents", "read");
  const history = useHistory();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  const [toggleCandidate, setToggleCandidate] = React.useState<any>(null);

  const handleToggleConfirm = () => {
    if (!toggleCandidate) return;
    dataProvider
      .update("localrepaircomponents", {
        id: toggleCandidate.id,
        data: { ...toggleCandidate, isActive: !toggleCandidate.isActive },
        previousData: toggleCandidate,
      })
      .then(() => {
        notify(
          toggleCandidate.isActive ? "Component deactivated successfully." : "Component activated successfully.",
          "success"
        );
        refresh();
      })
      .catch((error: any) => {
        notify(error?.message ?? "Failed to update status.", "error");
      })
      .finally(() => setToggleCandidate(null));
  };

  return (
    <>
      <ToggleStatusDialog
        record={toggleCandidate}
        onClose={() => setToggleCandidate(null)}
        onConfirm={handleToggleConfirm}
      />
      <PoolList
        {...props}
        title="Local Repair Components"
        perPage={DefaultPerPageSize}
        bulkActionButtons={false}
        filters={
          <FilterToolbar
            dataSource={[
              {
                source: "ownerCode",
                inputType: FilterInputType.Text,
                label: "Owner Code",
              },
              {
                source: "pn",
                inputType: FilterInputType.Text,
                label: "PN",
              },
              {
                source: "vendor",
                inputType: FilterInputType.Text,
                label: "Vendor",
              },
              {
                source: "isActive",
                inputType: FilterInputType.Select,
                label: "Is Active",
                choices: [
                  { id: true, name: "Active" },
                  { id: false, name: "Inactive" },
                ],
                resettable: true,
              },
            ]}
          />
        }
        exporter={false}
        actions={<ListActions />}
        filterDefaultValues={{ isActive__eq: true }}
      >
        <Datagrid optimized>
          <FunctionField
            label=""
            render={(record: any) => (
              <>
                <Tooltip title="Edit">
                  <IconButton
                    size="small"
                    onClick={e => {
                      e.stopPropagation();
                      history.push(`/localrepaircomponents/${record.id}`);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={record.isActive ? "Deactivate" : "Activate"}>
                  <IconButton
                    size="small"
                    onClick={e => {
                      e.stopPropagation();
                      setToggleCandidate(record);
                    }}
                    style={{ color: record.isActive ? "#c62828" : "#2e7d32" }}
                  >
                    <PowerSettingsNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          />
          <TextField source="ownerCode" label="Owner Code" />
          <TextField source="pn" label="PN" />
          <TextField source="pnDescription" label="Description" />
          <TextField source="vendor" label="Vendor" />
          <FunctionField
            label="Is Active"
            render={(record: any) =>
              record.isActive ? (
                <Chip
                  label="Active"
                  size="small"
                  style={{ backgroundColor: "#e8f5e9", color: "#2e7d32", fontWeight: 600 }}
                />
              ) : (
                <Chip
                  label="Inactive"
                  size="small"
                  style={{ backgroundColor: "#fce4ec", color: "#c62828", fontWeight: 600 }}
                />
              )
            }
          />
          <CreatedByField label="Created By" link={false} />
          <CustomDateField source="createdDate" showTime />
          <UpdatedByField label="Updated By" link={false} />
          <CustomDateField source="updatedDate" showTime />
        </Datagrid>
      </PoolList>
    </>
  );
};

export default LocalRepairComponentList;
